<template>
  <q-linear-progress v-if="isDataFetching" indeterminate />
  <PForm
    v-else
    ref="pForm"
    :show-header="false"
    :primary-cta="isStageEditable ? submitButton : {}"
    :secondary-cta="{}"
    :ternary-cta="formData.stage_cd == 'pending' ? skipButton : {}"
    body-class="q-px-2x-md q-pt-xl"
    @submit.prevent
  >
    <div class="col-12">
      <div class="flex items-center justify-between">
        <span class="text-md text-weight-medium text-neutral">
          {{ computedStage == "direct_verification" ? "Direct" : "Indirect" }}
          Verification
        </span>
        <PBtn flat class="q-pa-none hidden" @click="openSpreadsheet">
          <RedirectIcon class="icon--sm q-mr-xs" />
          <span class="text-sm text-weight-medium">Spreadsheet</span>
        </PBtn>
      </div>

      <q-separator color="neutral-40" />
    </div>
    <div class="text-xs text-neutral-70 q-mt-xxs">
      Checks for several data fields based on telephone, message, etc.
    </div>
    <div>
      <div class="row q-col-gutter-x-xxl q-mt-sm">
        <div class="col-6">
          <div class="text-sm text-neutral q-mt-xs">
            <span>
              {{
                computedStage == "direct_verification" ? "Direct" : "Indirect"
              }}
              Grade
            </span>
            <span class="text-negative">*</span>
          </div>
        </div>
        <div class="col-6">
          <PSelectInput
            v-model="formData.grade"
            placeholder="Select a grade"
            :options="unref(gradingOptions)"
            required
            :disable="!isStageEditable"
          />
        </div>
      </div>
      <div class="row q-col-gutter-x-xxl q-mt-sm">
        <div class="col-6">
          <div class="text-sm text-neutral">Outcome</div>
        </div>
        <div class="col-6">
          <PBadge
            :label="formData.outcome || 'pending'"
            :type="formData.outcome || 'pending'"
            class="text-capitalize q-py-xxs q-px-sm"
          />
        </div>
      </div>
      <div v-if="shouldShowRemarkField" class="col-12 q-mt-xxl q-mb-sm">
        <PInput
          v-model="formData.remarks"
          label="Remark"
          type="textarea"
          placeholder="Enter remark"
          autogrow
          :disable="!isStageEditable"
        />
      </div>
      <div v-if="shouldShowSkipReasonField" class="col-12 q-mt-xxl q-mb-sm">
        <PInput
          v-model="formData.reason"
          label="Skip reason"
          type="textarea"
          placeholder="Enter reason"
          autogrow
          disable
        />
      </div>
    </div>
  </PForm>
</template>

<script setup>
import RedirectIcon from "assets/icons/redirect.svg";
import directIndirectFormModel from "src/modules/user/models/directIndirectForm";
import PDialogForm from "src/components/PDialogForm.vue";

import { unref, watch } from "vue";
import { useQuasar } from "quasar";
const $q = useQuasar();

const { verificationCenterFormState, user } = storeToRefs(useUserStore());
const { getUserCreditVerification, putUserVerificationData } = useUserStore();

const pForm = ref(null);

const gradingData = ref(null);

const { formData, isLoading } = useForm({
  formModel: directIndirectFormModel(),
  entity: gradingData,
  formRef: pForm,
  isForm: verificationCenterFormState,
});

const computedStage = computed(() => props.stage);

const props = defineProps({
  stage: {
    type: String,
    required: true,
  },
});

const gradingOptions = ref(["A", "B", "C", "D", "E"]);

const computedGrade = computed(() => unref(formData).grade);

watch(computedGrade, (grade) => {
  if (grade == null || !grade.length) {
    unref(formData).outcome = "pending";
  } else if (grade == "A" || grade == "B") {
    unref(formData).outcome = "positive";
  } else if (grade == "*") {
    unref(formData).outcome = "skipped";
  } else {
    unref(formData).outcome = "negative";
  }
});

const handleCompleteAction = async () => {
  try {
    isLoading.value = true;

    const response = await putUserVerificationData({
      stage_name: unref(computedStage),
      outcome: unref(formData).outcome,
      stage_cd: "completed",
      grade: unref(formData).grade,
      remarks: unref(formData).remarks,
      reason: "",
    });

    gradingData.value = response?.data?.data;

    isLoading.value = false;
  } catch (error) {
    isLoading.value = false;

    console.error(error);
  }
};

const submitButton = computed(() => {
  return {
    disable:
      !unref(formData).grade?.length || unref(formData).grade == null
        ? true
        : false,
    loading: unref(isLoading),
    label: "Mark as complete",
    type: "submit",
    outline: true,
    fluid: true,
    onClick: () => handleCompleteAction(),
  };
});

const handleSkipAction = () => {
  const rejectDialog = $q.dialog({
    component: PDialogForm,
    componentProps: {
      title: `Skip ${
        unref(computedStage) == "direct_verification" ? "Direct" : "Indirect"
      } Verification Stage`,
      submitLabel: "Submit",
      form: {
        input: {
          placeholder: "Enter reason",
          label: "Reason to skip this stage",
          labelClasses: "text-sm text-neutral",
          validations: {
            label: "Rejection reason",
            required: true,
          },
        },
      },
      cancellable: false,
      onConfirm: async (rejectReason) => {
        try {
          isLoading.value = true;

          const response = await putUserVerificationData({
            stage_name: unref(computedStage),
            outcome: "skipped",
            stage_cd: "skipped",
            grade: "*",
            remarks: unref(formData).remarks,
            reason: rejectReason,
            manual_skip: true,
          });

          gradingData.value = response?.data?.data;

          isLoading.value = false;

          rejectDialog.hide();
        } catch (error) {
          isLoading.value = false;

          console.error(error);
        }
      },
    },
  });
};

const skipButton = computed(() => {
  return {
    loading: unref(isLoading),
    label: "Skip this stage",
    type: "type",
    flat: true,
    dense: true,
    fluid: true,
    onClick: () => handleSkipAction(),
  };
});

const openSpreadsheet = () => {
  let url =
    "https://docs.google.com/spreadsheets/d/1myX_wbuhtAgudep_f4hNfLLmiBYJSIgFgYSmn9VzGuo/edit?usp=sharing";

  window.open(url, "_blank");
};

const isDataFetching = ref(false);

const fetchUserCreditVerificationDetails = async () => {
  try {
    isDataFetching.value = true;

    const response = await getUserCreditVerification({
      params: {
        stage_name: unref(computedStage),
      },
    });

    gradingData.value = response?.data?.data;

    isDataFetching.value = false;
  } catch (error) {
    console.error(error);

    isLoading.value = false;
  }
};

const computedFormState = computed(
  () => unref(verificationCenterFormState).open,
);

const hasCreditRatingProvided = computed(() => {
  return (
    Array.isArray(unref(user)?.credit_ratings) &&
    unref(user).credit_ratings.length > 0
  );
});

const isApprovedManualSkippedStageEditable = computed(() => {
  return (
    unref(user)?.application_status === "approved" &&
    unref(user)?.background_check?.direct_indirect_grading_status_cd ===
      "completed" &&
    !unref(hasCreditRatingProvided) &&
    unref(formData)?.stage_cd === "skipped"
  );
});

const isStageEditable = computed(() => {
  return (
    unref(formData)?.stage_cd === "pending" ||
    unref(isApprovedManualSkippedStageEditable)
  );
});

const hasRealGradeSelected = computed(() => {
  const grade = unref(formData)?.grade;

  return !!grade && grade !== "*";
});

const shouldShowRemarkField = computed(() => {
  return (
    unref(formData)?.stage_cd !== "skipped" ||
    (unref(isApprovedManualSkippedStageEditable) && unref(hasRealGradeSelected))
  );
});

const shouldShowSkipReasonField = computed(() => {
  return (
    unref(formData)?.stage_cd === "skipped" && !unref(hasRealGradeSelected)
  );
});

watch(computedFormState, async (state) => {
  if (state) {
    await fetchUserCreditVerificationDetails();
  }
});

onMounted(async () => {
  await fetchUserCreditVerificationDetails();
});
</script>
